import { StandardForm, FormField, FormInputGroup, TextInput, FormError, TextArea, InputWithFloatingLabel, FloatingLabel, FlexBox, Button } from '@vp/swan';
import React, { useContext, useState, useEffect } from 'react';
import { ChatbotContext } from './src-contexts-ChatbotContext.js';
import { CAContext } from './src-contexts-CAContext.js';
import { MiawChatContext } from './src-contexts-MiawChatContext.js';
import { validateName, validateOrderNumber, validatechatbotContactReason, validateEmail } from './src-utils-validation-utils.js';
import SummaryFlowLogic from './src-components-PrechatFormInline-useSummaryFlowLogic.js';

const PrechatForm = ({ onSubmit, correctSummary, typeOfFLow }) => {
  const {
    state: { sessionId },
    setState: setChatbotState
  } = useContext(ChatbotContext);
  const {
    state: { startMiawChatSession }
  } = useContext(MiawChatContext);
  const {
    chatState: { userInfo }
  } = useContext(CAContext);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    chatbotContactReason: "",
    isAPICalled: false,
    orderNumber: ""
  });
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    chatbotContactReason: "",
    orderNumber: ""
  });
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    chatbotContactReason: false,
    orderNumber: false
  });
  const [autoFilled, setAutoFilled] = useState(false);
  const { handleSummaryGenerated } = SummaryFlowLogic({ formData, setAutoFilled, setFormData, correctSummary });
  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    let error = "";
    switch (field) {
      case "email":
        error = validateEmail(formData?.email || "");
        break;
      case "chatbotContactReason":
        error = validatechatbotContactReason(formData?.chatbotContactReason || "");
        break;
      case "orderNumber":
        error = validateOrderNumber(formData?.orderNumber || "");
        break;
      default:
        error = validateName(formData[field] || "", field === "firstName" ? "First name" : "Last name");
        break;
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
  };
  const validateForm = () => {
    const newErrors = {
      firstName: userInfo?.isLoggedIn && userInfo?.userData ? "" : validateName(formData.firstName || "", "First name"),
      lastName: userInfo?.isLoggedIn && userInfo?.userData ? "" : validateName(formData.lastName || "", "Last name"),
      email: userInfo?.isLoggedIn && userInfo?.userData ? "" : validateEmail(formData.email || ""),
      chatbotContactReason: validatechatbotContactReason(formData.chatbotContactReason || ""),
      orderNumber: typeOfFLow === "nonLoggedInPreChatWithOrderNumber" ? validateOrderNumber(formData.orderNumber || "") : ""
    };
    setErrors(newErrors);
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      chatbotContactReason: true,
      orderNumber: true
    });
    return Object.values(newErrors).every((e) => !e);
  };
  const handleSubmit = () => {
    if (!validateForm()) return;
    const userData = userInfo?.isLoggedIn && userInfo?.userData ? userInfo.userData : null;
    if (formData.orderNumber) {
      setChatbotState((prev) => ({
        ...prev,
        orderNumber: formData.orderNumber || ""
      }));
    }
    const payloadToSend = {
      email: userData?.email || formData.email || "",
      firstName: userData?.firstName || formData.firstName || "",
      lastName: userData?.lastName || formData.lastName || "",
      chatbotIdentifier: sessionId || "",
      chatbotContactReason: formData.chatbotContactReason || "",
      orderNumber: userData?.orderNumber?.trim()?.toUpperCase() || formData?.orderNumber?.trim()?.toUpperCase() || "",
      isSummaryGenerated: autoFilled,
      typeOfFLow: typeOfFLow || ""
    };
    onSubmit?.(payloadToSend);
    if (correctSummary) {
      return;
    }
    if (autoFilled) {
      handleSummaryGenerated();
    } else {
      startMiawChatSession?.(payloadToSend);
    }
  };
  useEffect(() => {
    if (userInfo?.isLoggedIn && userInfo.userData && autoFilled && formData.isAPICalled) {
      handleSubmit();
    }
  }, [formData.isAPICalled]);
  return formData.isAPICalled ? /* @__PURE__ */ React.createElement(StandardForm, { compactMode: true, marginY: "between-actions" }, !userInfo?.isLoggedIn && !correctSummary && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormField, { mb: "5" }, /* @__PURE__ */ React.createElement(FormInputGroup, null, /* @__PURE__ */ React.createElement(
    TextInput,
    {
      value: formData.firstName,
      onChange: (e) => setFormData({ ...formData, firstName: e.target.value }),
      onBlur: () => handleBlur("firstName"),
      placeholder: "First name"
    }
  ), touched.firstName && errors.firstName && /* @__PURE__ */ React.createElement(FormError, null, errors.firstName))), /* @__PURE__ */ React.createElement(FormField, { mb: "5" }, /* @__PURE__ */ React.createElement(FormInputGroup, null, /* @__PURE__ */ React.createElement(
    TextInput,
    {
      value: formData.lastName,
      onChange: (e) => setFormData({ ...formData, lastName: e.target.value }),
      onBlur: () => handleBlur("lastName"),
      placeholder: "Last name"
    }
  ), touched.lastName && errors.lastName && /* @__PURE__ */ React.createElement(FormError, null, errors.lastName))), /* @__PURE__ */ React.createElement(FormField, null, /* @__PURE__ */ React.createElement(FormInputGroup, null, /* @__PURE__ */ React.createElement(
    TextInput,
    {
      value: formData.email,
      onChange: (e) => setFormData({ ...formData, email: e.target.value }),
      onBlur: () => handleBlur("email"),
      placeholder: "Email linked to your VistaPrint account"
    }
  ), touched.email && errors.email && /* @__PURE__ */ React.createElement(FormError, null, errors.email))), typeOfFLow === "nonLoggedInPreChatWithOrderNumber" && /* @__PURE__ */ React.createElement(FormField, null, /* @__PURE__ */ React.createElement(FormInputGroup, null, /* @__PURE__ */ React.createElement(
    TextInput,
    {
      value: formData.orderNumber,
      onChange: (e) => setFormData({ ...formData, orderNumber: e.target.value }),
      onBlur: () => handleBlur("orderNumber"),
      placeholder: "Order number"
    }
  ), touched.orderNumber && errors.orderNumber && /* @__PURE__ */ React.createElement(FormError, null, errors.orderNumber)))), !autoFilled && !correctSummary && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormField, null, /* @__PURE__ */ React.createElement(FormInputGroup, null, /* @__PURE__ */ React.createElement(
    TextArea,
    {
      fullWidth: true,
      value: formData.chatbotContactReason,
      onChange: (e) => setFormData({ ...formData, chatbotContactReason: e.target.value }),
      onBlur: () => handleBlur("chatbotContactReason"),
      placeholder: "Contact reason"
    }
  ), touched.chatbotContactReason && errors.chatbotContactReason && /* @__PURE__ */ React.createElement(FormError, null, errors.chatbotContactReason)))), correctSummary && /* @__PURE__ */ React.createElement(FormField, null, /* @__PURE__ */ React.createElement(FormInputGroup, null, /* @__PURE__ */ React.createElement(InputWithFloatingLabel, null, /* @__PURE__ */ React.createElement(FloatingLabel, null, "Contact reason"), /* @__PURE__ */ React.createElement(
    TextArea,
    {
      pt: 7,
      fullWidth: true,
      value: formData.chatbotContactReason,
      onChange: (e) => setFormData({ ...formData, chatbotContactReason: e.target.value }),
      onBlur: () => handleBlur("chatbotContactReason"),
      placeholder: "Contact reason",
      rows: 5,
      resize: "both"
    }
  )), touched.chatbotContactReason && errors.chatbotContactReason && /* @__PURE__ */ React.createElement(FormError, null, errors.chatbotContactReason))), /* @__PURE__ */ React.createElement(FlexBox, { justifyContent: "space-around" }, /* @__PURE__ */ React.createElement(Button, { onClick: handleSubmit, skin: "primary" }, "Confirm"))) : null;
};

export { PrechatForm as default };
//# sourceMappingURL=src-components-PrechatFormInline-PrechatFormInline.js.map
